import {useEffect} from 'react';
import {ArrayUtils, constants, LogUtils} from 'src/js/common';
import {useApiQuery} from 'src/js/hooks/useApiQuery';
import {useAppDispatch, useAppSelector} from 'src/js/store_hooks';

import {ENUM_REDUCER_STATE_ID} from '../enum_constants';
import {enumSlice} from '../enum_slice';
import {EnumMap} from '../enum_types';

const logger = LogUtils.getLogger('useLoadEnums', constants.LIBRARY_NAME);

export interface UseLoadEnumsReturnValue {
  isLoading: boolean;
  error: unknown;
}

export function useLoadEnums(apiUrl: string, id: string, isHierarchical: boolean): UseLoadEnumsReturnValue {
  const isInitialized = useAppSelector((state) => state[ENUM_REDUCER_STATE_ID].isInitialized[id]);

  const {data, isLoading, error} = useApiQuery<EnumMap>(
    [apiUrl],
    null,
    {
      axiosConfig: {url: apiUrl},
      reactQueryOptions: {
        enabled: !isInitialized,
      },
    },
    false
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (data && !isInitialized) {
      let mappedData = data;
      if (isHierarchical) {
        const newData = Object.entries(data).map(([enumName, enumValues]) => [
          enumName,
          ArrayUtils.computeHierarchyMetadata(enumValues, (v) => ({id: v.name, parentId: v.parentName})),
        ]);
        mappedData = Object.fromEntries(newData);
      }
      dispatch(enumSlice.actions.addEnums({enumMap: mappedData, id}));
    }
  }, [id, data, isInitialized, isHierarchical, dispatch]);

  useEffect(() => {
    if (error) {
      logger.warn(`Error when loading enums!\n ${error}`);
    }
  }, [error]);

  return {isLoading, error};
}
